'use client';

import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { AppWindow, ExternalLink, Maximize2, Minus, MoreHorizontal, Pin, PinOff, X } from 'lucide-react';
import { Avatar } from '@/components/avatar';
import {
  useCurrentScreenId,
  useDesktopFullscreen,
  useWorkspaceActions,
  useWorkspaceItems,
} from '@/components/workspace/store';
import {
  FileContextMenu,
  useFileContextMenu,
  type ContextMenuItem,
} from '@/components/workspace/panels/file-context-menu';
import {
  uniqueParticipants,
  useParticipantsMirror,
} from '@/components/workspace/presence/presence-bridge';
import {
  getPlacement,
  isOnCanvas,
  type WorkspaceItemDTO,
} from '@/lib/workspaces/types';
import { entryOfItem, orderScreens } from '@/lib/workspaces/screens';
import { displayScreenEntries } from '@/lib/workspaces/stage-rules';
import { itemKindLabel, parseWidgetIcon, resolveContentKind } from '@/lib/content/app-identity';
import { WidgetMark, kindVisual } from '@/components/workspace/panels/content-preview';
import {
  UNNAMED_ITEM_LABEL,
  resolveItemLabel,
  useLiveContentMeta,
  type LiveContentMeta,
} from '@/components/workspace/item-label';
import { cn } from '@/lib/utils';
import { useRailLayout } from './rail-item';
import { useT } from '@/components/i18n-provider';

/** Keep in sync with the canvas GRID_MINOR (24px lattice). */
const GRID = 24;
/** Vertical gap between pucks — matches the container's `gap-1`. */
const GAP = 4;
/** Where rail-anchored popovers (the "…" flyout, the hover card) start:
 *  just clear of the 56px rail. Keep in step with RAIL_WIDTH_PX. */
const RAIL_EDGE_PX = 64;
/** Puck size tiers: shrink before overflowing (Dock behavior). */
const SIZE_TIERS = [36, 30, 26] as const;

/**
 * Identity chain for a taskbar entry, at any square size: agent avatar →
 * the app's declared MARK (manifest lucide/art) → live thumbnail → the
 * document format's glyph → cube. Shared between the puck and its hover
 * card so the two always agree on what the app looks like.
 *
 * `live` is this tab's live content metadata (`useLiveContentMeta`): the
 * mark's initial letter comes from the item's NAME, so a renamed app must
 * not keep the old name's initial (the 2026-09-13 browser pass, see
 * components/workspace/item-label.ts).
 */
export function identityVisual(
  item: WorkspaceItemDTO,
  iconBox: number,
  live: LiveContentMeta,
) {
  const username = item.state.cache?.username as string | undefined;
  const displayName = resolveItemLabel(item, live, UNNAMED_ITEM_LABEL);
  const previewUrl = item.state.cache?.previewUrl as string | undefined;
  const cache = item.state.cache as Record<string, unknown> | undefined;
  const appIconRef = parseWidgetIcon(cache?.appIcon as string | undefined);
  const markSeed =
    (cache?.widgetType as string | undefined) ?? item.refId ?? item.id;
  const cacheProjectMime = cache?.projectMime as string | undefined;
  const projVisual =
    !appIconRef && !previewUrl && cacheProjectMime
      ? kindVisual(
          resolveContentKind({
            contentType: 'folder',
            mimeType: '',
            metadata: { projectMime: cacheProjectMime },
          }),
        )
      : null;
  if (item.type === 'agent' && username) {
    return (
      <span
        className="shrink-0 overflow-hidden rounded-full"
        style={{ width: iconBox, height: iconBox }}
      >
        <Avatar
          seed={username}
          type="agent"
          src={
            (item.state.cache?.avatarUrl as string | null | undefined) ?? null
          }
          className="h-full w-full"
        />
      </span>
    );
  }
  if (appIconRef) {
    return (
      <WidgetMark
        iconRef={appIconRef}
        seed={markSeed}
        name={displayName}
        className="rounded-md"
        style={{ width: iconBox, height: iconBox }}
      />
    );
  }
  if (previewUrl) {
    return (
      // eslint-disable-next-line @next/next/no-img-element
      <img
        src={previewUrl}
        alt=""
        className="shrink-0 rounded-md object-cover"
        style={{ width: iconBox, height: iconBox }}
      />
    );
  }
  if (projVisual) {
    return (
      <span
        className={cn(
          'inline-flex shrink-0 items-center justify-center rounded-md',
          projVisual.tone,
        )}
        style={{ width: iconBox, height: iconBox }}
      >
        <projVisual.Icon className="h-4 w-4" strokeWidth={1.75} />
      </span>
    );
  }
  return (
    <span
      className="inline-flex shrink-0 items-center justify-center rounded-md bg-muted text-muted-foreground"
      style={{ width: iconBox, height: iconBox }}
    >
      <AppWindow className="h-4 w-4" />
    </span>
  );
}

function kindLabel(item: WorkspaceItemDTO): string {
  if (item.type === 'agent') return 'Agent';
  if (item.type === 'content') {
    // Registry-resolved: a design says "Vector Design", a deck "Presenter".
    return itemKindLabel({
      contentType: (item.state.cache?.contentType as string | undefined) ?? null,
      mimeType: (item.state.cache?.mimeType as string | undefined) ?? null,
      projectMime: (item.state.cache?.projectMime as string | undefined) ?? null,
    });
  }
  if (item.type === 'terminal' || item.type === 'ssh') return 'Terminal';
  if (item.type === 'python') return 'Python';
  if (item.type === 'files') return 'Files';
  if (item.type === 'daemon') return 'Daemon';
  return 'App';
}

/**
 * The rail taskbar (window-management v2, Part IV): APPS ONLY — one
 * puck per open window, in the spatial screen order. Blank screens are
 * places, not running apps; they live in the overview and the screen
 * chords, never here. Minimized entries stay listed, dimmed; the
 * window on the current screen reads active; peers' dots mark
 * collaborators. Click = go there; hover = preview card; drag a puck
 * onto another to SWAP the two windows' positions. Overflow shrinks
 * pucks through size tiers, then collapses the tail into a "…" flyout —
 * a scrollbar never appears (P4). While ⌘⇧ is held each puck shows the
 * NUMBER OF ITS SCREEN (split partners share one), matching the
 * ⌘⇧digit chords, which address screens left to right.
 */
export function RailDock({ chordHeld = false }: { chordHeld?: boolean }) {
  const t = useT();
  const items = useWorkspaceItems();
  // Names come from LIVE content metadata, falling back to the stored item
  // snapshot. Read-only: a renamed app must relabel the taskbar without this
  // tab writing the fresh name into the shared doc (item-label.ts says why).
  const live = useLiveContentMeta();
  const currentScreenId = useCurrentScreenId();
  // The windows this device shows full screen (lane D; the Stage algorithm,
  // rule 17): their pucks carry a full-screen badge, and their screen
  // numbers are their temporary screens'.
  const fullscreen = useDesktopFullscreen();
  const { patchItemState, removeItem } = useWorkspaceActions();
  const participants = useParticipantsMirror();
  // Taskbar entries render as full-width labeled rows only in the wide
  // (mobile drawer) variant; the compact desktop rail shows centered pucks.
  const { variant } = useRailLayout();
  const expanded = variant === 'wide';
  const ctx = useFileContextMenu<WorkspaceItemDTO>();

  // Drag-to-swap + hover-preview + overflow-flyout local state.
  const [dragId, setDragId] = useState<string | null>(null);
  const [dropId, setDropId] = useState<string | null>(null);
  const [hover, setHover] = useState<{ id: string; top: number } | null>(null);
  const [flyoutOpen, setFlyoutOpen] = useState(false);
  // Where the "…" button sits, and where the panel ends up. Kept apart on
  // purpose: the panel is placed from its MEASURED height (below), so the
  // anchor has to survive re-measuring.
  const [flyoutAnchor, setFlyoutAnchor] = useState(0);
  const [flyoutTop, setFlyoutTop] = useState(200);
  const flyoutRef = useRef<HTMLDivElement | null>(null);
  const hoverTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(
    () => () => {
      if (hoverTimer.current) clearTimeout(hoverTimer.current);
    },
    [],
  );
  const clearHover = () => {
    if (hoverTimer.current) clearTimeout(hoverTimer.current);
    hoverTimer.current = null;
    setHover(null);
  };
  // Focus or the pointer moving INTO an app's iframe blurs this window
  // without any puck event firing (the frame is another document), so the
  // card would outlive the hover. The window's own blur is the one signal
  // that covers it.
  useEffect(() => {
    const onWindowBlur = () => {
      if (hoverTimer.current) clearTimeout(hoverTimer.current);
      hoverTimer.current = null;
      setHover(null);
    };
    window.addEventListener('blur', onWindowBlur);
    return () => window.removeEventListener('blur', onWindowBlur);
  }, []);

  // Height budget = the space the rail ACTUALLY leaves the taskbar, between
  // the nav cluster above and the avatar below. It used to be 40% of the
  // viewport, which on a tall window is far less than the rail has free — the
  // strip collapsed into "…" with half the rail standing empty. The wrapper is
  // `flex-1 min-h-0`, so its own height IS the free space (flex sizes it from
  // the line, not from its content — no feedback loop with the tier math).
  // HYDRATION: initialize with the SSR constant and measure on MOUNT — a
  // `typeof window` initializer measured during hydration, so any viewport
  // that disagreed with 480px picked a different size tier on the client than
  // the server HTML and React flagged the tree.
  const stripRef = useRef<HTMLDivElement | null>(null);
  const [availH, setAvailH] = useState(480);
  useEffect(() => {
    const el = stripRef.current;
    if (!el) return;
    const measure = () => {
      const h = el.getBoundingClientRect().height;
      if (h > 0) setAvailH(Math.floor(h));
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    window.addEventListener('resize', measure);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, []);

  // Place the overflow panel against the "…" button using its MEASURED
  // height. It used to clamp against a hardcoded 240px, so near the foot of a
  // tall rail the panel was shoved ~150px ABOVE the button it belongs to even
  // when it held two icons. Anchor its TOP to the button, then only pull it up
  // if it would run off the bottom.
  useLayoutEffect(() => {
    if (!flyoutOpen) return;
    const place = () => {
      const el = flyoutRef.current;
      if (!el) return;
      const h = el.getBoundingClientRect().height;
      const maxTop = window.innerHeight - h - 8;
      setFlyoutTop(Math.max(8, Math.min(flyoutAnchor - 8, maxTop)));
    };
    place();
    window.addEventListener('resize', place);
    return () => window.removeEventListener('resize', place);
  }, [flyoutOpen, flyoutAnchor]);

  // Apps only (Part IV): boards are screens, not tasks.
  const tiles = orderScreens(items).filter((t) => t.type !== 'board');
  // Screen numbers for the ⌘⇧ keycaps — the ENTRY list the digit
  // chords address (splits collapse to one number, blanks count too, and
  // a window shown full screen on this device has a screen of its own).
  const fullscreenIds = fullscreen.map((r) => r.id);
  const entries = displayScreenEntries(items, fullscreenIds);

  const capacityFor = (s: number) =>
    Math.max(1, Math.floor((availH + GAP) / (s + GAP)));
  let tileSize: number = SIZE_TIERS[0];
  for (const s of SIZE_TIERS) {
    tileSize = s;
    if (capacityFor(s) >= tiles.length) break;
  }
  const cap = capacityFor(tileSize);
  const needOverflow = !expanded && tiles.length > cap;
  const visibleTiles = expanded
    ? tiles
    : needOverflow
      ? tiles.slice(0, cap - 1)
      : tiles;
  const overflowTiles = needOverflow ? tiles.slice(visibleTiles.length) : [];

  const goTo = (item: WorkspaceItemDTO) => {
    window.dispatchEvent(
      new CustomEvent('ws:screen', {
        detail: { kind: 'item', itemId: item.id },
      }),
    );
  };

  // Swapping two taskbar entries = swapping the two windows' positions
  // on the canvas (center-preserving, grid-snapped) — order is spatial,
  // so the taskbar order follows the geometry, never a hidden list.
  const swapScreens = (aId: string, bId: string) => {
    const a = items.find((it) => it.id === aId);
    const b = items.find((it) => it.id === bId);
    if (!a || !b || a.id === b.id) return;
    const snap = (v: number) => Math.round(v / GRID) * GRID;
    const acx = a.state.x + a.state.width / 2;
    const acy = a.state.y + a.state.height / 2;
    const bcx = b.state.x + b.state.width / 2;
    const bcy = b.state.y + b.state.height / 2;
    patchItemState(a.id, {
      x: snap(bcx - a.state.width / 2),
      y: snap(bcy - a.state.height / 2),
    });
    patchItemState(b.id, {
      x: snap(acx - b.state.width / 2),
      y: snap(acy - b.state.height / 2),
    });
  };

  const menuItems = (target: WorkspaceItemDTO): ContextMenuItem[] => {
    const placement = getPlacement(target.state);
    const isKept = placement === 'pinned';
    const onCanvas = isOnCanvas(target.state);
    return [
      {
        id: 'show',
        label: onCanvas ? t('workspace.rail.bringIntoView') : t('workspace.rail.restore'),
        icon: <ExternalLink className="h-4 w-4" />,
        onSelect: () => goTo(target),
      },
      ...(onCanvas
        ? [
            {
              id: 'minimize',
              label: 'Minimize',
              icon: <Minus className="h-4 w-4" />,
              onSelect: () =>
                patchItemState(target.id, { placement: 'docked' }),
            } satisfies ContextMenuItem,
          ]
        : []),
      {
        id: 'keep',
        label: isKept ? t('workspace.rail.unpinFromDock') : t('workspace.rail.pinToDock'),
        icon: isKept ? (
          <PinOff className="h-4 w-4" />
        ) : (
          <Pin className="h-4 w-4" />
        ),
        onSelect: () =>
          patchItemState(target.id, {
            placement: isKept ? 'canvas' : 'pinned',
          }),
      },
      {
        id: 'close',
        label: 'Close',
        icon: <X className="h-4 w-4" />,
        onSelect: () => void removeItem(target.id),
      },
    ];
  };

  if (tiles.length === 0) return null;

  const hoverTile = hover ? tiles.find((t) => t.id === hover.id) : null;
  const hoverPeers = hoverTile
    ? uniqueParticipants(participants.filter((p) => p.screenItemId === hoverTile.id))
    : [];

  const renderTile = (item: WorkspaceItemDTO, size: number) => {
    const displayName = resolveItemLabel(item, live, UNNAMED_ITEM_LABEL);
    const placement = getPlacement(item.state);
    const isKept = placement === 'pinned';
    const minimized = placement === 'docked';
    const isActive = item.id === currentScreenId && !minimized;
    const peers = uniqueParticipants(
      participants.filter((p) => p.screenItemId === item.id),
    );
    const iconBox = Math.max(20, size - 8);
    const entry = entryOfItem(entries, item.id);
    const screenNo = entry ? entries.indexOf(entry) + 1 : 0;
    const visual = identityVisual(item, iconBox, live);
    const isFullscreen = fullscreenIds.includes(item.id);

    return (
      <button
        key={item.id}
        type="button"
        // The hover card goes away on ANY act on the puck: click, pointer
        // down, context menu, focus leaving. The pointer stays on the puck
        // after a click, so `onMouseLeave` alone never fired and the card
        // sat over the app's own Menu button at the top of its window (the
        // Revent e2e run of 2026-09-26/27). Clearing also cancels a dwell
        // timer still counting, so a quick click never opens the card late.
        onClick={() => {
          clearHover();
          goTo(item);
        }}
        onPointerDown={clearHover}
        onBlur={clearHover}
        onContextMenu={(e) => {
          clearHover();
          ctx.bindTrigger(item).onContextMenu(e);
        }}
        aria-label={
          isFullscreen
            ? `Go to ${displayName} (${t('workspace.node.fullScreen')})`
            : `Go to ${displayName}`
        }
        aria-current={isActive ? 'true' : undefined}
        draggable={!expanded}
        onDragStart={(e) => {
          clearHover();
          setDragId(item.id);
          e.dataTransfer.setData('text/ws-screen', item.id);
          e.dataTransfer.effectAllowed = 'move';
        }}
        onDragEnd={() => {
          setDragId(null);
          setDropId(null);
        }}
        onDragOver={(e) => {
          if (!dragId || dragId === item.id) return;
          e.preventDefault();
          e.dataTransfer.dropEffect = 'move';
          setDropId(item.id);
        }}
        onDragLeave={() => setDropId((cur) => (cur === item.id ? null : cur))}
        onDrop={(e) => {
          e.preventDefault();
          const src = e.dataTransfer.getData('text/ws-screen') || dragId;
          if (src && src !== item.id) swapScreens(src, item.id);
          setDragId(null);
          setDropId(null);
        }}
        onMouseEnter={(e) => {
          if (expanded || dragId) return;
          if (hoverTimer.current) clearTimeout(hoverTimer.current);
          const top = e.currentTarget.getBoundingClientRect().top;
          hoverTimer.current = setTimeout(() => {
            hoverTimer.current = null;
            setHover({
              id: item.id,
              top: Math.min(
                Math.max(8, top - 8),
                (typeof window !== 'undefined' ? window.innerHeight : 800) -
                  190,
              ),
            });
          }, 350);
        }}
        onMouseLeave={clearHover}
        className={cn(
          'group relative inline-flex items-center rounded-xl outline-none transition-colors',
          'hover:bg-accent/60 focus-visible:ring-2 focus-visible:ring-primary/50',
          expanded ? 'h-9 w-full gap-3 px-2.5' : 'justify-center',
          // Expanded (drawer) rows are full width, so a fill reads there.
          isActive && expanded && 'bg-accent/80',
          minimized && 'opacity-55 hover:opacity-90',
          dropId === item.id && 'ring-2 ring-primary/60',
          dragId === item.id && 'opacity-40',
        )}
        style={expanded ? undefined : { width: size, height: size }}
      >
        {/* Which app you are actually looking at. A background can't show it —
            the button is exactly the tile's size, so the app's own icon covers
            it — and padding the tile would cost taskbar capacity. A rail-edge
            bar costs no space and is the familiar "you are here" mark. */}
        {isActive && !expanded && (
          <span
            aria-hidden
            className="pointer-events-none absolute -left-0.5 top-1/2 h-4 w-[3px] -translate-y-1/2 rounded-full bg-foreground"
          />
        )}
        {visual}
        {expanded && (
          <span className="flex-1 truncate text-left text-sm text-foreground/80">
            {displayName}
          </span>
        )}
        {/* Full screen on this device (lane D; the Stage algorithm, rule 17:
            "the rail puck and the Stage card carry a full-screen badge").
            BOTTOM-LEFT: the left edge's middle is the active-screen bar,
            top-right the peers, bottom-right the pin and the chord keycap. */}
        {isFullscreen && (
          <span
            aria-hidden
            data-testid="rail-puck-fullscreen-badge"
            className="pointer-events-none absolute -bottom-0.5 -left-0.5 inline-flex h-3.5 w-3.5 items-center justify-center rounded-full bg-foreground text-background ring-2 ring-background"
          >
            <Maximize2 className="h-2 w-2" strokeWidth={2.5} />
          </span>
        )}
        {isKept && (
          <span
            aria-hidden
            className="absolute bottom-0.5 right-0.5 inline-flex h-3 w-3 items-center justify-center rounded-full bg-primary text-primary-foreground ring-2 ring-background"
          >
            <Pin className="h-1.5 w-1.5" />
          </span>
        )}
        {peers.length > 0 && (
          // TOP-RIGHT in the compact rail: the left edge belongs to the
          // active-screen bar. Bottom-right is taken by the pin / chord chip.
          <span
            aria-hidden
            className={cn(
              'absolute flex -space-x-1',
              expanded ? 'right-2' : '-right-1 -top-1',
            )}
          >
            {/* Never more than two slots — a third 14px circle overflows a
                36px puck. Beyond two people the second slot becomes a count,
                so the badge width is fixed however many are here. */}
            {peers.slice(0, peers.length > 2 ? 1 : 2).map((p) => (
              // The REAL avatar (presence carries `avatarUrl`, same as the
              // remote cursors). The only ring is the background, so stacked
              // avatars stay separable without a coloured halo round each
              // face. `Avatar` falls back to a generated mark by seed, so
              // someone without a picture still gets a stable identity.
              <span
                key={p.user.peerId}
                className="inline-flex h-3.5 w-3.5 overflow-hidden rounded-full"
                style={{ boxShadow: '0 0 0 2px var(--color-background)' }}
              >
                <Avatar
                  seed={p.user.username || p.user.peerId}
                  type="human"
                  src={p.user.avatarUrl ?? null}
                  className="h-full w-full"
                />
              </span>
            ))}
            {peers.length > 2 && (
              // Muted, not solid foreground: it is a count, not a person, and
              // it should not out-shout the avatar beside it.
              <span
                className="flex h-3.5 min-w-3.5 items-center justify-center rounded-full bg-muted px-0.5 text-[8px] font-semibold leading-none text-muted-foreground"
                style={{ boxShadow: '0 0 0 2px var(--color-background)' }}
              >
                +{peers.length - 1}
              </span>
            )}
          </span>
        )}
        {chordHeld && !expanded && screenNo >= 1 && screenNo <= 9 && (
          <span
            aria-hidden
            className="pointer-events-none absolute -bottom-0.5 -right-0.5 z-10 inline-flex h-4 min-w-4 items-center justify-center rounded-[5px] border border-border/60 bg-background/95 px-0.5 font-mono text-[10px] font-medium leading-none text-foreground/80 shadow-sm"
          >
            {screenNo}
          </span>
        )}
      </button>
    );
  };

  return (
    <>
      <span aria-hidden className="my-1 block h-1.5 w-full" />
      {/* No overflow clipping here: the size-tier math above is the
          no-scroll guarantee, and `overflow-hidden` was slicing the top
          off the first puck's presence badge (-top-1 needs headroom). */}
      <div
        ref={stripRef}
        className={cn(
          'flex min-h-0 w-full flex-1 flex-col gap-1',
          expanded ? 'items-stretch' : 'items-center',
        )}
        role="list"
        aria-label={t('workspace.rail.openApps')}
      >
        {visibleTiles.map((item) => (
          <div
            role="listitem"
            key={item.id}
            className={expanded ? undefined : 'flex w-full flex-col items-center'}
          >
            {renderTile(item, tileSize)}
          </div>
        ))}
        {needOverflow && (
          <button
            type="button"
            aria-label={`${overflowTiles.length} more apps`}
            aria-expanded={flyoutOpen}
            onClick={(e) => {
              setFlyoutAnchor(e.currentTarget.getBoundingClientRect().top);
              setFlyoutOpen((v) => !v);
            }}
            className="inline-flex items-center justify-center rounded-xl text-muted-foreground transition-colors hover:bg-accent/60"
            style={{ width: tileSize, height: tileSize }}
          >
            <MoreHorizontal className="h-4 w-4" />
          </button>
        )}
      </div>
      {/* Overflow flyout — the tail of the taskbar as a grid. */}
      {flyoutOpen &&
        typeof document !== 'undefined' &&
        createPortal(
          <>
            <div
              aria-hidden
              className="fixed inset-0 z-[118]"
              onClick={() => setFlyoutOpen(false)}
            />
            <div
              ref={flyoutRef}
              className="fixed z-[119] grid grid-cols-4 gap-1.5 rounded-xl border border-border/60 bg-popover p-2 shadow-xl"
              style={{ left: RAIL_EDGE_PX, top: flyoutTop }}
              role="list"
              aria-label={t('workspace.rail.moreApps')}
            >
              {overflowTiles.map((item) => (
                <div role="listitem" key={item.id} onClick={() => setFlyoutOpen(false)}>
                  {renderTile(item, 34)}
                </div>
              ))}
            </div>
          </>,
          document.body,
        )}
      {/* Hover preview card — portaled to <body>: the glass rail's
          backdrop-filter creates a containing block that would trap a
          `fixed` child. */}
      {hoverTile &&
        hover &&
        typeof document !== 'undefined' &&
        createPortal(
          <div
            aria-hidden
            className="pointer-events-none fixed z-[120] w-56 rounded-xl border border-border/60 bg-popover p-2.5 shadow-xl"
            style={{ left: RAIL_EDGE_PX, top: hover.top }}
          >
            {(hoverTile.state.cache?.previewUrl as string | undefined) ? (
              // eslint-disable-next-line @next/next/no-img-element
              <img
                src={hoverTile.state.cache?.previewUrl as string}
                alt=""
                className="mb-2 aspect-video w-full rounded-lg border border-border/40 object-cover"
              />
            ) : null}
            {/* Identity header: the SAME visual chain as the puck, at card
                size, beside the name — so the card confirms what the tiny
                puck could only hint at. */}
            <div className="flex items-center gap-2.5">
              {identityVisual(hoverTile, 32, live)}
              <div className="min-w-0">
                <div className="truncate text-[13px] font-medium text-foreground">
                  {resolveItemLabel(hoverTile, live, UNNAMED_ITEM_LABEL)}
                </div>
                {/* Numbers are keys, not names (F5): digits show only as
                    keycaps while ⌘⇧ is held — never in copy. */}
                <div className="text-[11px] text-muted-foreground">
                  {kindLabel(hoverTile)}
                  {getPlacement(hoverTile.state) === 'docked' ? ' · Minimized' : ''}
                </div>
              </div>
            </div>
            {/* Collaborators: REAL avatars here (the card has the room the
                14px puck dot lacks) + @usernames, the address form used
                everywhere else in the product. */}
            {hoverPeers.length > 0 && (
              <div className="mt-1.5 flex items-center gap-1.5 text-[11px] text-muted-foreground">
                <span className="flex -space-x-1">
                  {hoverPeers.slice(0, 3).map((p) => (
                    <Avatar
                      key={p.user.peerId}
                      seed={p.user.username || p.user.peerId}
                      type="human"
                      src={p.user.avatarUrl ?? null}
                      className="h-4 w-4 rounded-full ring-1 ring-background"
                    />
                  ))}
                </span>
                <span className="truncate">
                  {hoverPeers.map((p) => `@${p.user.username}`).join(' ')}
                </span>
              </div>
            )}
          </div>,
          document.body,
        )}
      <FileContextMenu
        anchor={ctx.anchor}
        onClose={ctx.close}
        items={ctx.target ? menuItems(ctx.target) : []}
        ariaLabel="Open app menu"
      />
    </>
  );
}
